import React, { useRef, useEffect, useCallback } from 'react';
import { isSoleLinkSection } from '../../../utils/linkSectionUtils';
import { SubMenuContainerProps, TopNavLinkSection } from '../types';
import SubMenuView from './sub-menu-view';

const SubMenuContainer = ({
  activeMenu,
  activeSubMenu,
  children,
  emitNavClick,
  firstSubMenuRef,
  menu,
  onClickLink,
  onCloseMenu,
  setActiveSubMenu,
  onReturnToMainMenu,
}: SubMenuContainerProps) => {
  const subMenuRefs = useRef<Record<string, HTMLElement | null>>({});

  // Focus the active sub-menu item when it changes
  useEffect(() => {
    if (activeSubMenu) {
      subMenuRefs.current[activeSubMenu]?.focus();
    }
  }, [activeSubMenu]);

  const onSetActiveSubMenu = useCallback((subMenuName: string | undefined) => {
    if (subMenuName) {
      emitNavClick({
        nav_item: menu.name,
        nav_item_hierarchy_2: subMenuName,
      });
    }
    setActiveSubMenu(subMenuName);
  }, [emitNavClick, menu.name, setActiveSubMenu]);

  const onSingleSectionLinkClick = useCallback((
    e: React.MouseEvent<HTMLAnchorElement>,
    section: TopNavLinkSection,
  ) => {
    emitNavClick({
      nav_item: menu.name,
      nav_item_hierarchy_2: section.name,
    });
    onClickLink();
  }, [emitNavClick, menu.name, onClickLink]);

  const onReturnToSubMenu = useCallback(() => {
    // Close the link list panel and return focus to the sub-menu item
    if (activeSubMenu) {
      subMenuRefs.current[activeSubMenu]?.focus();
    }
  }, [activeSubMenu]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, currentSubMenu: string, currentIndex: number) => {
    const subMenuNames = menu.linkSections.map((section) => section.name);
    const currentSection = menu.linkSections.find((s) => s.name === currentSubMenu);
    const soleLinkSection = isSoleLinkSection(currentSection);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % subMenuNames.length;
        const nextSubMenu = subMenuNames[nextIndex];
        subMenuRefs.current[nextSubMenu]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = currentIndex === 0 ? subMenuNames.length - 1 : currentIndex - 1;
        const prevSubMenu = subMenuNames[prevIndex];
        subMenuRefs.current[prevSubMenu]?.focus();
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        // Open the sub-menu panel if not already open
        if (activeSubMenu !== currentSubMenu) {
          setActiveSubMenu(currentSubMenu);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        // Close the sub-menu panel if open, or return to main menu
        onReturnToMainMenu();
        break;
      }
      case 'Enter':
      case ' ': {
        if (soleLinkSection) {
          break;
        }
        e.preventDefault();
        setActiveSubMenu(currentSubMenu);
        break;
      }
      case 'Tab': {
        // Allow natural tab behavior
        if (e.shiftKey) {
          // Shift+Tab should return to main menu
          e.preventDefault();
          onReturnToMainMenu();
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        onCloseMenu();
        break;
      }
      case 'Home': {
        e.preventDefault();
        const firstSubMenu = subMenuNames[0];
        subMenuRefs.current[firstSubMenu]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        const lastSubMenu = subMenuNames[subMenuNames.length - 1];
        subMenuRefs.current[lastSubMenu]?.focus();
        break;
      }
      default:
        break;
    }
  }, [activeSubMenu, menu.linkSections, onCloseMenu, onReturnToMainMenu, setActiveSubMenu]);

  return (
    <SubMenuView
      activeMenu={activeMenu}
      activeSubMenu={activeSubMenu}
      firstSubMenuRef={firstSubMenuRef}
      handleKeyDown={handleKeyDown}
      menu={menu}
      onCloseMenu={onCloseMenu}
      onReturnToSubMenu={onReturnToSubMenu}
      onSingleSectionLinkClick={onSingleSectionLinkClick}
      setActiveSubMenu={onSetActiveSubMenu}
      subMenuRefs={subMenuRefs}
    >
      {children}
    </SubMenuView>
  );
};

export default SubMenuContainer;
